import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import { LivePreviewDiagnostics,LivePreviewEligibility, PreviewMode, SimulatePreviewResponse } from '../../types/previewStudio';
import DebugAccordionPanel, { NormalizedMetadata } from './DebugAccordionPanel';
import LivePreviewDiagnosticPanel from './LivePreviewDiagnosticPanel';

/**
 * Extract macro values from live preview ADM by parsing known attribute patterns.
 * Since Venice doesn't return macro replacement metadata, we infer them from the compiled ADM.
 */
function extractMacrosFromAdm(adm: string): Array<{ macro: string; value: string }> {
  const macros: Array<{ macro: string; value: string }> = [];

  // Extract data-dcm-click-tracker value → %{tURL0}
  const clickTrackerMatch = adm.match(/data-dcm-click-tracker=["']([^"']+)["']/i);
  if (clickTrackerMatch && clickTrackerMatch[1] && !clickTrackerMatch[1].includes('%{')) {
    macros.push({ macro: '%{tURL0}', value: clickTrackerMatch[1] });
  }

  // Extract im_token from data-dcm-keywords → %{TOKEN}
  const keywordsMatch = adm.match(/data-dcm-keywords=["']([^"']+)["']/i);
  if (keywordsMatch && keywordsMatch[1]) {
    const tokenMatch = keywordsMatch[1].match(/im_token=([^&"'\s]+)/);
    if (tokenMatch && tokenMatch[1] && !tokenMatch[1].includes('%{')) {
      macros.push({ macro: '%{TOKEN}', value: tokenMatch[1] });
    }
  }

  // Extract data-dcm-app-id → %{APP_BUNDLE}
  const appIdMatch = adm.match(/data-dcm-app-id=["']([^"']+)["']/i);
  if (appIdMatch && appIdMatch[1] && !appIdMatch[1].includes('%{')) {
    macros.push({ macro: '%{APP_BUNDLE}', value: appIdMatch[1] });
  }

  // Extract impression pixel URL (preview-e.imbid.co/image/) → impression tracking
  const impressionMatch = adm.match(/src=["'](https?:\/\/[^"']*(?:imbid\.co|inmarket\.com)\/image\/[^"']+)["']/i);
  if (impressionMatch && impressionMatch[1]) {
    macros.push({ macro: 'Impression Pixel', value: impressionMatch[1] });
  }

  // Extract redirect/click URL (preview-e.imbid.co/redirect/) from click tracker
  const redirectMatch = adm.match(/(https?:\/\/[^"']*(?:imbid\.co|inmarket\.com)\/redirect\/[^"'\s;]+)/i);
  if (redirectMatch && redirectMatch[1]) {
    macros.push({ macro: 'Click Redirect', value: redirectMatch[1] });
  }

  return macros;
}

interface DebugPanelProps {
  previewResponse: SimulatePreviewResponse | null;
  previewMode?: PreviewMode;
  liveEligibility?: LivePreviewEligibility | null;
  liveDiagnostics?: LivePreviewDiagnostics | null;
  checkingEligibility?: boolean;
  eligibilityError?: string | null;
  onRefreshEligibility?: () => void;
  onSwitchToSimulated?: () => void;
  onRetry?: () => void;
  fireTracking?: boolean;
  iframeRef?: React.RefObject<HTMLIFrameElement | null>;
}

export default function DebugPanel({
  previewResponse,
  previewMode = 'simulated',
  liveEligibility,
  liveDiagnostics,
  checkingEligibility = false,
  eligibilityError,
  onRefreshEligibility,
  onSwitchToSimulated,
  onRetry,
  fireTracking = false,
  iframeRef,
}: DebugPanelProps) {
  // Normalize metadata from SimulatePreviewResponse to NormalizedMetadata
  // NOTE: We use the backend-provided assets array directly.
  const normalizedMetadata: NormalizedMetadata = useMemo(() => {
    if (!previewResponse) {
      return {
        warnings: [],
        macros_replaced: [],
      };
    }

    const { metadata } = previewResponse;
    const assets = metadata.assets ?? [];
    const strippedAssets = metadata.stripped_assets ?? [];

    const hasWarnings = (metadata.warnings ?? []).length > 0;
    const hasUnresolvedMacros = ((metadata as any).macros_unresolved ?? []).length > 0;
    const hasAdm = !!previewResponse.adm;
    const health = (metadata as any).health || (() => {
      if (hasWarnings || hasUnresolvedMacros) return 'warning';
      return 'ok';
    })();
    const summary = (metadata as any).summary || (() => {
      const parts: string[] = [];
      if (hasAdm) parts.push('ADM present');
      if (assets.length > 0) {
        const clickCount = assets.filter((a) => a.type === 'anchor' && a.purpose === 'click').length;
        const pixelCount = assets.filter((a) => a.type === 'img' && (a.purpose === 'impression' || a.purpose === 'sync')).length;
        const scriptCount = assets.filter((a) => a.type === 'script').length;
        if (clickCount) parts.push(`${clickCount} click URL${clickCount > 1 ? 's' : ''}`);
        if (pixelCount) parts.push(`${pixelCount} pixel${pixelCount > 1 ? 's' : ''}`);
        if (scriptCount) parts.push(`${scriptCount} script${scriptCount > 1 ? 's' : ''}`);
      }
      parts.push(`${hasWarnings ? metadata.warnings.length : 0} warning${hasWarnings && metadata.warnings.length !== 1 ? 's' : ''}`);
      return parts.join(', ') || 'No data available';
    })();

    const processedTag = previewResponse.adm;

    // For live preview, extract macros from ADM since Venice doesn't provide replacement metadata
    let macrosReplaced = metadata.macros_replaced ?? [];
    const isLivePreviewNote = macrosReplaced.length === 1 && macrosReplaced[0]?.macro === 'Note';

    if (isLivePreviewNote && processedTag) {
      // Replace the generic note with actual extracted macros
      const extractedMacros = extractMacrosFromAdm(processedTag);
      if (extractedMacros.length > 0) {
        macrosReplaced = extractedMacros;
      }
    }

    return {
      warnings: metadata.warnings ?? [],
      macros_replaced: macrosReplaced,
      macros_unresolved: (metadata as any).macros_unresolved,
      macro_coverage: (metadata as any).macro_coverage,
      assets,
      stripped_assets: strippedAssets,
      settings_applied: metadata.settings_applied,
      adm: processedTag
        ? {
            source: (previewResponse as any).adm_source,
            length: processedTag.length,
            contains_script: (processedTag.includes('<script') || processedTag.includes('&lt;script')) ?? false,
            contains_img: (processedTag.includes('<img') || processedTag.includes('&lt;img')) ?? false,
            contains_iframe: (processedTag.includes('<iframe') || processedTag.includes('&lt;iframe')) ?? false,
          }
        : undefined,
      processed_tag: processedTag || undefined,
      privacy: (metadata as any).privacy,
      health,
      summary,
      creative_type: metadata.creative_type,
      vendor: metadata.vendor,
      processed_vendor: (metadata as any).processed_vendor,
      size: metadata.size,
      // Variable replacement details
      creative_variables: metadata.creative_variables,
      location_variables: metadata.location_variables,
      processed_campaign_urls: metadata.processed_campaign_urls,
    };
  }, [previewResponse]);

  // on failure, shows diagnostic panel with metadata from last successful preview
  if (previewMode === 'live' && liveDiagnostics && onSwitchToSimulated && onRetry) {
    return (
      <LivePreviewDiagnosticPanel
        diagnostics={liveDiagnostics}
        onSwitchToSimulated={onSwitchToSimulated}
        onRetry={onRetry}
        metadata={normalizedMetadata}
        fireTracking={fireTracking}
      />
    );
  }

  if (!previewResponse) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Debug Console
        </Typography>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Generate a preview to see debug information
          </Typography>
        </Box>
      </Box>
    );
  }

  // Use preview.id as stable identifier, fallback to preview_url if id not available
  const previewId = previewResponse.preview?.id ?? previewResponse.preview_url;

  return (
    <DebugAccordionPanel
      metadata={normalizedMetadata}
      previewId={previewId}
      previewMode={previewMode}
      liveEligibility={liveEligibility}
      checkingEligibility={checkingEligibility}
      eligibilityError={eligibilityError}
      onRefreshEligibility={onRefreshEligibility}
      fireTracking={fireTracking}
      iframeRef={iframeRef}
    />
  );
}

